import { messageOptions, plainText } from "./helpMessage.mjs";

function formatStamp(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return String(timestamp);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function partLine(part) {
  if (!part || typeof part !== "object") return "";
  if (part.type === "file") return typeof part.label === "string" ? part.label : "";
  if (part.type === "link") {
    const label = typeof part.label === "string" ? part.label : "";
    const route = typeof part.route === "string" ? part.route : "";
    if (label && route) return `${label} (${route})`;
    return label || route;
  }
  return plainText(part.text || "");
}

function messageBody(message) {
  if (Array.isArray(message?.Parts) && message.Parts.length) {
    return message.Parts.map(partLine).join("");
  }
  return plainText(message?.Message || "");
}

function attachmentLine(message, files) {
  if (!Array.isArray(message?.FileIds) || !message.FileIds.length) return "";
  const names = message.FileIds
    .map((id) => files?.[id]?.Name)
    .filter((name) => typeof name === "string" && name);
  return names.length ? `Attachments: ${names.join(", ")}` : "";
}

function widgetLine(widget) {
  if (!widget || typeof widget !== "object") return "";
  const data = widget.widgetData;
  if (data && typeof data.label === "string" && typeof data.route === "string") {
    return `${data.label} (${data.route})`;
  }
  return typeof widget.widgetID === "string" && widget.widgetID ? `Widget: ${widget.widgetID}` : "";
}

function optionsLine(message) {
  const options = messageOptions(message);
  return options.length ? `Options: ${options.map((option, index) => `${index + 1}. ${option.label}`).join(", ")}` : "";
}

/** Readable transcript. Record tags are written as their numbers. */
export function conversationText(conversation, { botName = "Help", exportedAt } = {}) {
  const lines = [conversation?.Title || "New conversation"];
  if (exportedAt) lines.push(`Exported ${formatStamp(exportedAt)}`);
  const messages = Array.isArray(conversation?.Messages) ? conversation.Messages : [];
  if (!messages.length) {
    lines.push("", "No messages.");
    return `${lines.join("\n")}\n`;
  }
  for (const message of messages) {
    const speaker = message?.Role === "user" ? "You" : (botName || "Help");
    const extra = [attachmentLine(message, conversation?.Files), widgetLine(message?.Widget), optionsLine(message)].filter(Boolean);
    lines.push("", speaker, formatStamp(message?.Time), messageBody(message), ...extra);
  }
  return `${lines.join("\n")}\n`;
}

function copyParts(parts) {
  return parts.map((part) => {
    if (!part || typeof part !== "object") return {};
    const copy = {};
    if (typeof part.type === "string") copy.type = part.type;
    if (typeof part.text === "string") copy.text = part.text;
    if (typeof part.label === "string") copy.label = part.label;
    if (typeof part.route === "string") copy.route = part.route;
    if (typeof part.fileId === "string") copy.fileId = part.fileId;
    return copy;
  });
}

function messageData(message) {
  const record = {
    role: message?.Role === "user" ? "user" : "bot",
    time: message?.Time || null,
    message: typeof message?.Message === "string" ? message.Message : "",
  };
  if (Array.isArray(message?.Parts)) record.parts = copyParts(message.Parts);
  if (Array.isArray(message?.FileIds) && message.FileIds.length) record.fileIds = [...message.FileIds];
  if (message?.Widget && typeof message.Widget === "object") record.widget = message.Widget;
  const options = messageOptions(message);
  if (options.length) record.options = options;
  if (message?.Role === "user" && message.Call && typeof message.Call.tool === "string") {
    record.call = { tool: message.Call.tool, args: message.Call.args && typeof message.Call.args === "object" ? message.Call.args : {} };
  }
  return record;
}

const FILE_FIELDS = [
  ["Name", "name"],
  ["ContentType", "contentType"],
  ["Size", "size"],
  ["Source", "source"],
  ["Kind", "kind"],
  ["Status", "status"],
  ["CreatedAt", "createdAt"],
  ["Error", "error"],
  ["ReportType", "reportType"],
  ["Judgement", "judgement"],
  ["Text", "text"],
  ["Description", "description"],
];

function fileData(files) {
  if (!files || typeof files !== "object") return [];
  return Object.entries(files)
    .filter(([, file]) => file && typeof file === "object")
    .map(([id, file]) => {
      const record = { id };
      for (const [from, to] of FILE_FIELDS) {
        if (file[from] !== undefined && file[from] !== null) record[to] = file[from];
      }
      if (Array.isArray(file.Sources)) {
        record.sources = file.Sources
          .filter((source) => source && typeof source.label === "string")
          .map((source) => ({ label: source.label, route: typeof source.route === "string" ? source.route : "" }));
      }
      return record;
    });
}

/**
 * Structured copy of the conversation.
 * Storage paths and the search-index id stay out of the file.
 */
export function conversationData(conversation, { id = null, exportedAt = null } = {}) {
  return {
    id,
    title: conversation?.Title || "New conversation",
    createdAt: conversation?.CreatedAt || null,
    updatedAt: conversation?.UpdatedAt || null,
    exportedAt,
    messages: (Array.isArray(conversation?.Messages) ? conversation.Messages : []).map(messageData),
    files: fileData(conversation?.Files),
  };
}

export function exportFilename(title, extension) {
  const slug = String(title || "conversation")
    .replace(/[^\w\s-]+/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);
  return `${slug || "conversation"}.${extension === "json" ? "json" : "txt"}`;
}
